import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSelectedWorkspace } from '../../stores/workspaceStore';
import { routeBase, useCollection, studyName } from '../api/collections';
import type { ApiError } from '../../features/collections/api';
import { UploadApiError, fetchStudyImages } from '../../features/upload/api';
import { canKeepFiles, droppedFiles, pickFiles } from '../../features/upload/fileHandles';
import { LONE_MRXS, groupSlides, noSlidedatReason } from '../../features/upload/slides';
import { CannotUploadHere, CollectionGone } from '../ui/Gone';
import { Button, TextLink } from '../ui/Button';
import { Notice } from '../ui/Controls';
import { IconAlertTriangle, IconApplyAll, IconCamera, IconCheck, IconFileText, IconMicroscope, IconRadiation, IconUpload, IconX2 } from '../ui/icons';
import { toast } from '../ui/toast';
import { Breadcrumb, StepIndicator } from './UploadStudyPage';
import { ImageFields } from './DetailsFields';
import { reselectMessage, type AttachResult, certainKindOf, filePct, finishGate, fmtSize, importRoute, missingDetails, needsFile, openUpload, sessionById, sessionForStudy, sessionRoute, stepOneRoute, targetStudy, useUpload, type UploadFile, type UploadSession } from './store';

/**
 * Uploader step 2, "Upload" (`is.upload`): drop zone, the Images card (file
 * rail + detail panel with the five per-image fields), footer status and
 * Finish. Column 1120px.
 *
 * Every image here exists on the server already (made as its file is added),
 * and its details are saved as they are typed; only the anatomic site is
 * asked for. Finish writes nothing: it moves to the progress page, and
 * uploads still running carry on.
 */
/** The file rail's status line and colour: the transfer while it runs, then where the server has the image. */
function rowStatus(f: UploadFile): { label: string; color: string } {
  switch (f.phase) {
    case 'queued':
    case 'starting':
      return { label: `Uploading · ${filePct(f)}%`, color: '#8a8a8f' };
    case 'uploading':
    case 'finishing':
      return { label: `Uploading · ${filePct(f)}%`, color: '#0f0f10' };
    case 'failed':
      return { label: f.storageFull ? 'Out of storage' : 'Upload failed', color: '#d9342b' };
    case 'waiting-file':
      return f.storageFull ? { label: 'Out of storage', color: '#d9342b' } : { label: 'Select the file again', color: '#9a6400' };
    case 'done':
      if (f.stage === 'published') return { label: 'Imported', color: '#2f6b3a' };
      if (f.stage === 'failed') return { label: 'Could not be imported', color: '#d9342b' };
      // The bytes are in and nothing waits for the person: the server is importing it.
      if (f.site || f.stage === 'converting' || f.stage === 'ingesting') return { label: `Importing · ${f.percent ?? 0}%`, color: '#6b6b70' };
      // Its anatomic site still to give (the row says "Details missing"): the import waits for it.
      return { label: 'Uploaded', color: '#2f6b3a' };
  }
}
const ACCEPT = '.svs,.ndpi,.tif,.tiff,.scn,.dcm,.mrxs,.jpg,.jpeg,.png,.zip,.pdf';
/** The fields a refusal is shown beside; one about anything else goes under them. */
const SHOWN_FIELDS = ['site', 'laterality', 'type', 'stain', 'description'];

export function UploadFilesPage() {
  const { publicId } = useParams<{ publicId: string }>();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { current } = useSelectedWorkspace();
  const { data: col, error: colError } = useCollection(publicId);
  // The session is named by its study (`study`): one per study, so a reload,
  // the top bar and Back all find the same one.
  const studyParam = params.get('study');
  const found = useUpload((st) => sessionForStudy(st.sessions, studyParam));
  const s = found && found.colId === publicId ? found : null;
  const { removeFile, retry, setFileField, flushDetails, sync } = useUpload();
  const patch = (p: Partial<UploadSession>) => {
    if (s) useUpload.getState().patch(s.id, p);
  };
  const cancel = () => {
    if (s) useUpload.getState().cancel(s.id);
  };
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragDepth, setDragDepth] = useState(0);
  const [finishing, setFinishing] = useState(false);
  const grpId = params.get('group');
  const group = col?.groups.find((g) => g.public_id === grpId) ?? null;
  const locationName = group ? group.name : col?.name ?? '';
  const back = () => nav(`${routeBase()}/collections/${publicId}`);
  // A new upload's first step; a session's own is stepOneRoute.
  const stepOne = `${routeBase()}/collections/${publicId}/upload${grpId ? `?group=${grpId}` : ''}`;
  // Started from a study's page: the page names that study, and Cancel goes
  // back to it rather than to the collection.
  const fromStudy = s?.fromStudy && s.studyId ? s.studyId : null;
  const target = fromStudy ? col?.studies.find((x) => x.public_id === fromStudy) : undefined;
  const toStudy = () => nav(`${routeBase()}/studies/${encodeURIComponent(fromStudy ?? '')}`);
  const studyId = s ? targetStudy(s) : null;

  // Settled once per address: the dropdown leads from one study's files page
  // to another's, and the page (the same component) opens that one's session.
  const here = `${publicId}|${studyParam ?? ''}`;
  const settledFor = useRef<string | null>(null);
  useEffect(() => {
    if (settledFor.current === here) return;
    // A reload keeps the session (store.ts restores it) but not the bytes.
    // With none for the study, one starts on it here, and picks up the
    // study's unfinished images (sync, adopt); an address without a study
    // starts over at the first step.
    if (s) {
      settledFor.current = here;
      // Opened from a link without the group: put it back in the address so
      // the page, its breadcrumb and Back agree with the session.
      if ((s.grpId ?? null) !== (grpId ?? null)) nav(sessionRoute(routeBase(), s), { replace: true });
      return;
    }
    const study = studyParam;
    if (study && publicId) {
      // Only a study of this collection: wait for it to load to know.
      if (!col) return;
      // Not theirs to upload into: the page renders the refusal, and no
      // session is started or replaced on the way there.
      if (col.editable !== true) {
        settledFor.current = here;
        return;
      }
      if (col.studies.some((x) => x.public_id === study)) {
        settledFor.current = here;
        const opened = sessionById(useUpload.getState().sessions, openUpload({ colId: publicId, grpId: grpId ?? null, studyId: study, locationName }));
        // Already being sent from another collection's page (the study moved): that page.
        if (opened && opened.colId !== publicId) nav(sessionRoute(routeBase(), opened), { replace: true });
        return;
      }
      // Not in this collection any more (moved or deleted): choose again.
    }
    settledFor.current = here;
    nav(stepOne, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [col, here]);

  // The progress page and the pill name the upload by this; kept on the session.
  useEffect(() => {
    if (s && locationName && s.locationName !== locationName) useUpload.getState().patch(s.id, { locationName });
  }, [s, locationName]);

  // What the server has for the study, once per visit: stages, transfers
  // whose complete landed before a reload, images it removed — and the
  // study's unfinished images from an earlier visit, so they can be finished
  // here. The store's poll keeps it current after that.
  const sessionId = s?.id;
  useEffect(() => {
    if (!studyId) return;
    let live = true;
    const askedAt = Date.now();
    fetchStudyImages(studyId)
      .then((images) => {
        if (live) sync(studyId, images, { askedAt, adopt: true });
      })
      .catch((err) => {
        if (!live || !(err instanceof UploadApiError) || err.status !== 404) return;
        cancel();
        toast('The study this upload was going into no longer exists.');
        back();
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, studyId]);

  const colGone = (colError as ApiError | null)?.status === 404;
  // Let the session go with its destination. Kept, it has nowhere to be
  // filed, and the top bar would go on listing an upload whose only way
  // forward is the panel below, which has nothing to offer but the way out.
  useEffect(() => {
    if (colGone && sessionId) useUpload.getState().cancel(sessionId);
  }, [colGone, sessionId]);

  // Kept files the browser must ask about before they are read (store.ts reopenKept).
  const reopenable = useUpload((st) => st.reopenable);
  // The browser's picker failed here once (refused in a frame, blocked by policy): the input from then on.
  const pickerFailed = useRef(false);

  // The collection was deleted under this page: say so, rather than showing a
  // cached copy of it and failing at transfer time.
  if (colGone) return <CollectionGone body="It was deleted while this page was open, so there is nowhere to file these images." />;
  // Step two is its own address, so it asks the same question step one does.
  if (col && col.editable !== true) return <CannotUploadHere collectionId={publicId} />;
  // Its session is being opened (the effect above), or it is another page's.
  if (!s || !col || !studyId) return <div style={{ flex: 1, background: '#fafaf9' }} />;
  // Refused for want of storage: said once, above the images, with the way to
  // room. After a reload they also need their file, but room is what they wait for.
  const noRoom = s.files.filter((f) => f.storageFull && (f.phase === 'failed' || f.phase === 'waiting-file'));
  const noRoomYours = noRoom.some((f) => f.storageFull === 'yours');
  const noRoomHeld = noRoom.filter((f) => f.file);
  const reselect = s.files.filter((f) => needsFile(f) && !f.storageFull);
  const announced = (r: AttachResult) => [r.resumed ? `${r.resumed} continuing where ${r.resumed > 1 ? 'they' : 'it'} left off` : '', r.replaced ? `${r.replaced} started over (different bytes)` : ''].filter(Boolean);
  const announce = (r: AttachResult) => {
    const parts = announced(r);
    if (parts.length) toast(parts.join(' · '));
  };
  const pick = (list: File[]) => {
    if (!list.length) return;
    // One way in for every file: a re-selected file resumes its row, a
    // namesake with other bytes replaces it, anything else is new. The files
    // of one slide (a MIRAX with its folder, the .dcm levels of one series) go in as one (slides.ts).
    void groupSlides(list, { dcm: true, mrxs: true }).then(({ picked, loneMirax, noSlidedat }) => {
      // One toast shows at a time, so what a drop left out, and why, shares it with what resumed.
      const heldBack = [
        loneMirax.length ? `${loneMirax.map((f) => f.name).join(', ')}: ${LONE_MRXS}` : '',
        noSlidedat.length ? `${noSlidedat.map((f) => f.name).join(', ')}: ${noSlidedatReason(noSlidedat.length)}` : '',
      ].filter(Boolean);
      const said = [...heldBack, ...(picked.length ? announced(useUpload.getState().attachFiles(s.id, picked)) : [])];
      // What to do instead takes longer to read than a count.
      if (said.length) toast(said.join(' · '), undefined, heldBack.length ? 12_000 : undefined);
    });
  };
  // The browser's own picker where it keeps files for later, else the input.
  const browse = () => {
    if (!canKeepFiles() || pickerFailed.current) {
      inputRef.current?.click();
      return;
    }
    void pickFiles(ACCEPT).then((picked) => {
      if (picked !== null) return pick(picked);
      pickerFailed.current = true;
      inputRef.current?.click();
    });
  };
  // One click: every kept file the browser must ask about, asked at once.
  const resume = () => void useUpload.getState().reopenKept(true).then(announce);
  const file = s.files.find((f) => f.id === s.fileId) ?? null;
  const { missing, missingCount, refused, failedCount, waitingCount, uploadingCount, problems, ready, pct } = finishGate(s);
  const footerNote = ready
    ? uploadingCount
      ? 'Ready · uploads carry on after you finish'
      : 'Ready'
    : problems.length
      ? problems[0]
      : refused.length
        ? `${refused[0].name}: ${refused[0].detailsError!.message}`
        : [failedCount ? `${failedCount} failed` : '', waitingCount ? `${waitingCount} to select again` : '', uploadingCount ? `${uploadingCount} still uploading` : ''].filter(Boolean).join(' · ');
  const summary = s.files.length ? `${s.files.length} file${s.files.length > 1 ? 's' : ''} · ${pct}% uploaded${uploadingCount || waitingCount ? ' · uploading…' : ''}` : 'no files yet';
  // Missing details (the site) block Finish, so they read in amber with an icon, not grey;
  // clicking steps through the images that still need them.
  const nextMissing = () => {
    const i = missing.findIndex((f) => f.id === s.fileId);
    patch({ fileId: missing[(i + 1) % missing.length].id });
  };
  const dragging = dragDepth > 0;

  const finish = async () => {
    setFinishing(true);
    // Details typed a moment ago go first; a refusal keeps the person here, at the field.
    await flushDetails();
    setFinishing(false);
    const now = sessionById(useUpload.getState().sessions, s.id);
    const study = now && targetStudy(now);
    if (now && study && finishGate(now).ready) nav(importRoute(routeBase(), now.colId, study));
  };

  /** What the server said about this image's details, next to the field it named. */
  const refusal = (f: UploadFile, field: string, hint: string | null = null): ReactNode => {
    const text = f.detailsError?.field === field ? f.detailsError.message : hint ? `Remove ${hint}.` : null;
    return text ? <span style={{ color: '#d9342b' }}>{text}</span> : undefined;
  };

  const fileRow = (f: UploadFile) => {
    const on = f.id === s.fileId;
    const { label: status, color: statusColor } = rowStatus(f);
    const bar = f.phase === 'failed' || f.stage === 'failed' ? '#d9342b' : f.phase === 'done' ? '#2f6b3a' : '#0f0f10';
    const published = f.stage === 'published';
    return (
      <div key={f.id} className="tr-hover-white" onClick={() => patch({ fileId: f.id })} style={{ display: 'grid', gridTemplateColumns: '34px minmax(0,1fr) 24px', alignItems: 'center', gap: 10, padding: '10px 12px', cursor: 'pointer', background: on ? '#fff' : 'transparent', boxShadow: on ? 'inset 3px 0 0 #0f0f10' : 'none', borderBottom: '1px solid #f0f0ee' }}>
        <div style={{ width: 34, height: 34, borderRadius: 8, background: '#f2f2f0', display: 'grid', placeItems: 'center' }}>
          {f.certainType === 'radiography' ? <IconRadiation size={18} color="#0f0f10" /> : f.kind === 'wsi' ? <IconMicroscope size={18} color="#0f0f10" /> : f.kind === 'document' ? <IconFileText size={18} color="#0f0f10" /> : <IconCamera size={18} color="#0f0f10" />}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <span className="tr-ellipsis" style={{ fontSize: 13, fontWeight: 500, minWidth: 0 }}>{f.name}</span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: '#8a8a8f', whiteSpace: 'nowrap' }}>{f.members?.length ? `${f.members.length} files · ` : ''}{f.size ? fmtSize(f.size) : ''}</span>
          </div>
          <div style={{ height: 3, borderRadius: 2, background: '#ececea', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${filePct(f)}%`, background: bar }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 11.5, color: statusColor }}>{status}</span>
            {needsFile(f) ? (
              <TextLink onClick={browse} fontSize={11.5} underline hover="none" style={{ lineHeight: 1 }}>Select</TextLink>
            ) : f.phase === 'failed' && f.file ? (
              <TextLink onClick={() => retry(f.id)} fontSize={11.5} underline hover="none" style={{ lineHeight: 1 }}>Retry</TextLink>
            ) : f.detailsError ? (
              <span style={{ fontSize: 11, color: '#d9342b' }}>Details not saved</span>
            ) : missingDetails(f) ? (
              <span style={{ fontSize: 11, color: '#9a6400' }}>Details missing</span>
            ) : null}
          </div>
        </div>
        <button type="button" className="tr-hover-track-ink" disabled={published} title={published ? 'Imported: delete it from the study’s page' : 'Remove this image'} onClick={(e) => { e.stopPropagation(); removeFile(f.id); }} style={{ width: 24, height: 24, display: 'grid', placeItems: 'center', border: 0, borderRadius: 6, background: 'transparent', color: '#8a8a8f', cursor: published ? 'default' : 'pointer', opacity: published ? 0.4 : 1 }}>
          <IconX2 size={13} />
        </button>
      </div>
    );
  };

  return (
    <div
      style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#fafaf9' }}
      onDragEnter={(e) => { e.preventDefault(); setDragDepth((d) => d + 1); }}
      onDragLeave={(e) => { e.preventDefault(); setDragDepth((d) => Math.max(0, d - 1)); }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); setDragDepth(0); void droppedFiles(e.dataTransfer, ACCEPT).then(pick); }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 28px 0' }}>
        <Breadcrumb wsName={current?.name ?? ''} locationName={locationName} onBack={back} last="Upload" study={fromStudy ? { name: target ? studyName(target) : 'Study', onOpen: toStudy } : undefined} />
        <Button variant="outlined" size={32} title="Stops the uploads in progress. Images already uploaded stay in the study and finish on their own once they have a site; unfinished ones are removed after 24 hours." onClick={() => { cancel(); if (fromStudy) toStudy(); else back(); }}>Cancel</Button>
      </div>
      {!s.fromStudy ? <StepIndicator step={2} /> : null}
      <div className="tr-page" style={{ flex: 1, display: 'flex', justifyContent: 'center', padding: '24px 28px 40px' }}>
        <div style={{ width: '100%', maxWidth: 1120, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div
            className="tr-hover-drop"
            onClick={browse}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '34px 24px', border: `1.5px dashed ${dragging ? '#0f0f10' : '#d4d4d2'}`, borderRadius: 14, background: dragging ? '#fff' : '#fafaf9', cursor: 'pointer', textAlign: 'center' }}
          >
            <input ref={inputRef} type="file" multiple accept={ACCEPT} style={{ display: 'none' }} onClick={(e) => e.stopPropagation()} onChange={(e) => { pick(Array.from(e.target.files ?? [])); e.target.value = ''; }} />
            <div style={{ width: 44, height: 44, borderRadius: 12, background: '#f2f2f0', display: 'grid', placeItems: 'center' }}>
              <IconUpload size={20} color="#0f0f10" round />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 15, fontWeight: 600 }}>Drop images here, or <span style={{ textDecoration: 'underline', textUnderlineOffset: 3 }}>browse</span></span>
              <span style={{ fontSize: 12.5, color: '#6b6b70' }}>Scanned images (SVS, NDPI, SCN, TIFF, DICOM, MIRAX), flat images (JPG, PNG) and documents (PDF). A slide of several files (DICOM levels, a MIRAX with its folder) is dropped as its folder.</span>
            </div>
          </div>
          {reselect.length ? (
            <Notice tone="warn">
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{reselect.length === 1 ? `${reselect[0].name} needs to be selected again` : `${reselect.length} files need to be selected again`}</span>
                <span style={{ fontSize: 12.5 }}>
                  {reopenable
                    ? `The upload was interrupted. Resume opens the same ${reopenable === 1 ? 'file' : 'files'} again once your browser allows it, and each continues from the parts that already arrived.`
                    : reselect.length === 1 && reselect[0].error ? reselect[0].error : 'Their upload did not finish. Select them again (drop or browse) and each continues from the parts that already arrived.'}
                </span>
              </div>
              {reopenable ? (
                <Button variant="primary" size={30} px={12} fontSize={12.5} radius={6} onClick={resume}>Resume {reopenable === 1 ? 'upload' : `${reopenable} uploads`}</Button>
              ) : null}
              <Button variant="outlined" size={30} px={12} fontSize={12.5} radius={6} onClick={browse}>Select {reselect.length === 1 ? (reselect[0].members ? 'its files' : 'the file') : 'the files'}</Button>
            </Notice>
          ) : null}
          {noRoom.length ? (
            <Notice tone="danger">
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{noRoomYours ? 'You’re out of storage' : 'This workspace is out of storage'}</span>
                <span style={{ fontSize: 12.5 }}>
                  {noRoom.length === 1 ? `${noRoom[0].name} did not fit` : `${noRoom.length} files did not fit`}, so nothing of {noRoom.length === 1 ? 'it' : 'them'} was sent.{' '}
                  {noRoomYours ? 'Free up space by deleting images you no longer need.' : 'Ask the workspace’s owner to free up space.'}{' '}
                  {noRoomHeld.length === noRoom.length
                    ? `${noRoom.length === 1 ? 'It is' : 'They are'} sent by ${noRoom.length === 1 ? 'itself' : 'themselves'} as soon as there is room.`
                    : 'Once there is room, select the files again to send them.'}
                </span>
              </div>
              {noRoomYours ? (
                <Button variant="outlined" size={30} px={12} fontSize={12.5} radius={6} onClick={() => nav(`${routeBase()}/account/usage`)}>Free up space</Button>
              ) : null}
              {noRoomHeld.length ? (
                <Button variant="outlined" size={30} px={12} fontSize={12.5} radius={6} onClick={() => noRoomHeld.forEach((f) => retry(f.id))}>Retry {noRoomHeld.length === 1 ? 'upload' : `${noRoomHeld.length} uploads`}</Button>
              ) : (
                <Button variant="outlined" size={30} px={12} fontSize={12.5} radius={6} onClick={browse}>Select {noRoom.length === 1 ? 'the file' : 'the files'}</Button>
              )}
            </Notice>
          ) : null}
          <div style={{ background: '#fff', border: '1px solid #ebebea', borderRadius: 12 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '18px 24px 0' }}>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Images</h2>
              <span style={{ fontSize: 13, color: '#8a8a8f' }}>{summary}</span>
            </div>
            <div className="tr-stack" style={{ display: 'grid', gridTemplateColumns: '320px minmax(0,1fr)', marginTop: 14, borderTop: '1px solid #f0f0ee' }}>
              <div style={{ background: '#fafaf9', borderRight: '1px solid #f0f0ee', display: 'flex', flexDirection: 'column', borderBottomLeftRadius: 12 }}>{s.files.map(fileRow)}</div>
              <div style={{ padding: '16px 24px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                {file ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontSize: 14, fontWeight: 600 }}>{file.name}</span>
                        <span style={{ fontSize: 12, color: '#8a8a8f' }}>{file.certainType === 'radiography' ? 'X-ray' : file.kind === 'wsi' ? 'Scanned image' : file.kind === 'document' ? 'Document' : 'Flat image'} · details for this image only</span>
                      </div>
                      <Button variant="outlined" size={32} icon={<IconApplyAll size={14} />} onClick={() => { const n = useUpload.getState().applyToAll(s.id); toast(`Details applied to ${n} other ${n === 1 ? 'image' : 'images'}`); }}>
                        Apply to all images
                      </Button>
                    </div>
                    {/* A file waiting to be selected again is said once, above the images (with Resume). */}
                    {file.phase === 'failed' ? (
                      <Notice tone="danger">
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                          <span style={{ fontSize: 13, fontWeight: 600 }}>{file.storageFull ? 'Out of storage' : 'Upload failed'}</span>
                          <span style={{ fontSize: 12.5 }}>{file.error ?? reselectMessage(file)}</span>
                        </div>
                        {file.file ? (
                          <Button variant="outlined" size={30} px={12} fontSize={12.5} radius={6} onClick={() => retry(file.id)}>Retry upload</Button>
                        ) : null}
                        <Button variant="destructive" size={30} px={10} fontSize={12.5} onClick={() => removeFile(file.id)}>Remove</Button>
                      </Notice>
                    ) : file.stage === 'failed' ? (
                      <Notice tone="danger">
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                          <span style={{ fontSize: 13, fontWeight: 600 }}>Could not be imported</span>
                          <span style={{ fontSize: 12.5 }}>{file.stageError ?? 'The file could not be imported.'} Re-export the file and upload it again.</span>
                        </div>
                        <Button variant="destructive" size={30} px={10} fontSize={12.5} onClick={() => removeFile(file.id)}>Remove</Button>
                      </Notice>
                    ) : null}
                    <ImageFields values={file} certain={certainKindOf(file)} certainType={file.certainType ?? null} onField={(k, v) => setFileField(file.id, k, v)} said={(field, hint) => refusal(file, field, hint)} />
                    {file.detailsError && !SHOWN_FIELDS.includes(file.detailsError.field ?? '') ? <Notice tone="danger">{file.detailsError.message}</Notice> : null}
                  </>
                ) : (
                  <div style={{ flex: 1, display: 'grid', placeItems: 'center', minHeight: 300, color: '#8a8a8f', fontSize: 13.5 }}>Add images to start.</div>
                )}
              </div>
            </div>
          </div>
          <div className="tr-wrap-sm" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingTop: 4 }}>
            {!s.fromStudy ? (
              <Button variant="outlined" size={36} px={14} onClick={() => nav(stepOneRoute(routeBase(), s))}>Back</Button>
            ) : (
              <span />
            )}
            <div className="tr-wrap-sm" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12 }}>
              {footerNote ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: ready ? '#2f6b3a' : problems.length || refused.length ? '#d9342b' : '#8a8a8f' }}>
                  {ready ? <IconCheck size={14} sw={2.4} /> : null}
                  <span>{footerNote}</span>
                </div>
              ) : null}
              {missingCount ? (
                <TextLink onClick={nextMissing} title="Show the next image missing details (its anatomic site)" color="#9a6400" fontSize={13} weight={400} hover="underline" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <IconAlertTriangle size={14} sw={2.4} />
                  {missingCount} image{missingCount > 1 ? 's' : ''} missing details
                </TextLink>
              ) : null}
              <Button variant="primary" size={36} title="Available once every image has its anatomic site. Uploads still running carry on." disabled={!ready || finishing} onClick={() => void finish()}>
                Finish
              </Button>
            </div>
          </div>
        </div>
      </div>
      {problems.length > 1 ? (
        <div style={{ position: 'fixed', right: 24, bottom: 24, width: 360, maxWidth: 'calc(100vw - 48px)', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {problems.slice(1).map((p) => (
            <Notice key={p} tone="danger">{p}</Notice>
          ))}
        </div>
      ) : null}
    </div>
  );
}
